import { useMemo, useState } from "react";
import { deserializeEditGraph, resolveEdit } from "@desk/core";
import { PACK_REGISTRY } from "../content/registry.js";
import { CONSTRAINT_LABELS } from "../game/challenge/constraints.js";
import { formatTicksAsTime, buildEditedTimeline } from "../shell/lib/timeline.js";
import {
  ArtifactPreview,
  type ArtifactPreviewStatus,
} from "../shell/ui/ArtifactPreview.js";
import { FlowChrome } from "../shell/ui/FlowChrome.js";
import { ConstraintChips, FlowEmpty } from "../shell/ui/FlowStates.js";
import type { GameController } from "../shell/hooks/useGameController.js";

export function ChallengeLandingScreen({ game }: { game: GameController }) {
  const { state, dispatch } = game;
  const ch = state.challenge;
  const [previewStatus, setPreviewStatus] = useState<ArtifactPreviewStatus>("idle");

  const entry = ch ? PACK_REGISTRY[ch.packId] : null;
  const graph = useMemo(() => {
    if (!ch) return null;
    try {
      return deserializeEditGraph(ch.challengerGraphJson);
    } catch {
      return null;
    }
  }, [ch]);

  const resolved = useMemo(() => {
    if (!entry || !graph) return null;
    return resolveEdit(graph, entry.pack);
  }, [entry, graph]);

  const timeline = useMemo(() => {
    if (!entry || !graph || !resolved) return null;
    return buildEditedTimeline(entry.pack, resolved);
  }, [entry, graph, resolved]);

  const editedDuration = timeline
    ? formatTicksAsTime(timeline.durationTicks, entry!.pack.tickRate)
    : "–";

  if (!ch || !entry) {
    return (
      <FlowChrome testId="screen-challenge-landing" stage="Challenge" title="Beat My Edit">
        <FlowEmpty message="Challenge missing." />
      </FlowChrome>
    );
  }

  const score = ch.challengerScore;
  const previewSettled =
    previewStatus === "ready" || previewStatus === "fallback" || previewStatus === "error";
  const canAccept = previewSettled;

  return (
    <FlowChrome
      testId="screen-challenge-landing"
      className="challenge-landing"
      stage="Challenge"
      title="Beat My Edit"
      hook="Same source. Fresh edit. Beat this Reach?"
      actions={
        <>
          <button
            type="button"
            className="primary"
            data-testid="accept-challenge"
            disabled={!canAccept}
            title={!canAccept ? "Waiting for challenger preview" : undefined}
            onClick={() =>
              dispatch({
                type: "ACCEPT_CHALLENGE",
                startedAtMs: typeof performance !== "undefined" ? performance.now() : Date.now(),
              })
            }
          >
            {canAccept ? "Accept challenge" : "Preparing preview…"}
          </button>
          <button
            type="button"
            data-testid="dismiss-challenge"
            onClick={() => dispatch({ type: "DISMISS_CHALLENGE" })}
          >
            Not now
          </button>
        </>
      }
    >
      <div className="challenge-hero">
        <ArtifactPreview
          packId={ch.packId}
          pack={entry.pack}
          graphJson={ch.challengerGraphJson}
          label="Challenger reel"
          scoreText={`Reach ${score.reach}`}
          testId="challenge-preview"
          onStatusChange={setPreviewStatus}
        />
      </div>

      <section className="challenge-story" data-testid="challenge-story">
        <h2>{entry.title}</h2>
        <p className="sr-only">{entry.pack.canonicalRecord.summary}</p>
      </section>

      <div className="challenge-stats" data-testid="challenge-stats">
        <div>
          <span className="score-label">Challenger Reach</span>
          <strong data-testid="challenger-reach">{score.reach}</strong>
        </div>
        <div>
          <span className="score-label">Length</span>
          <strong data-testid="challenger-duration">{editedDuration}</strong>
        </div>
      </div>

      <section className="challenge-constraints" data-testid="challenge-constraints">
        <h2 className="sr-only">Limits</h2>
        <ConstraintChips
          labels={ch.constraintIds.map((id) => CONSTRAINT_LABELS[id])}
          emptyLabel="Open edit, no limits"
        />
      </section>
    </FlowChrome>
  );
}
